import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Send,
  Loader2,
  Bot,
  User,
  X,
  MessageSquare,
  Minus,
  Maximize2,
  History,
  Plus,
  Trash2,
  Clock,
  ArrowLeft,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useAuth } from "../App";
import { getAIWSEndApiMap, getAIEndApiMap } from "../apiMap";
import axios from "axios";

interface Message {
  id: string;
  sender: "user" | "ai";
  text: string;
  timestamp: Date;
}

interface AIAssistantChatProps {
  projectId: string;
  isSidebar?: boolean;
  onClose?: () => void;
}

interface SessionInfo {
  session_id: string;
  created_at: string | null;
}

const AIAssistantChat: React.FC<AIAssistantChatProps> = ({ 
  projectId, 
  isSidebar = false,
  onClose 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [isHistoryView, setIsHistoryView] = useState(false);
  
  const { user } = useAuth();
  const socketRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen || isSidebar) {
      scrollToBottom();
    }
  }, [messages, isOpen, isMinimized, isSidebar]);

  // Fetch session history list
  const fetchSessions = useCallback(async () => {
    if (!projectId || !user?.id) return;
    try {
      const url = getAIEndApiMap("aiChatSessions")
        .replace(":projectId", projectId)
        .replace(":studentId", user.id);
      const res = await axios.get(url);
      const sessionsList: SessionInfo[] = res.data.sessions || [];
      setSessions(sessionsList);
      
      // Auto-load most recent if none selected
      if (!currentSessionId && sessionsList.length > 0) {
        // We'll call loadSession directly here
        const sessId = sessionsList[0].session_id;
        const histUrl = getAIEndApiMap("aiChatHistory").replace(":sessionId", sessId);
        const histRes = await axios.get(histUrl);
        const historyMessages = histRes.data.messages.map((m: any, idx: number) => ({
          id: `hist-${idx}`,
          sender: m.role === "user" ? "user" : "ai",
          text: m.content,
          timestamp: new Date()
        }));
        setMessages(historyMessages);
        setCurrentSessionId(sessId);
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
    }
  }, [projectId, user?.id, currentSessionId]);

  // Load messages for a specific session
  const loadSession = async (sessionId: string) => {
    setIsLoading(true);
    try {
      const url = getAIEndApiMap("aiChatHistory").replace(":sessionId", sessionId);
      const res = await axios.get(url);
      const historyMessages = res.data.messages.map((m: any, idx: number) => ({
        id: `hist-${idx}`,
        sender: m.role === "user" ? "user" : "ai",
        text: m.content,
        timestamp: new Date()
      }));
      setMessages(historyMessages);
      setCurrentSessionId(sessionId);
      setIsHistoryView(false);
    } catch (err) {
      console.error("Error loading session history:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    const newSessionId = crypto.randomUUID();
    setCurrentSessionId(newSessionId);
    setMessages([]);
    setIsHistoryView(false);
  };

  // Initialize or handle session changes
  useEffect(() => {
    if ((isOpen || isSidebar) && projectId && user?.id && !currentSessionId) {
      fetchSessions();
    }
  }, [isOpen, isSidebar, projectId, user?.id, currentSessionId, fetchSessions]);

  useEffect(() => {
    if ((isOpen || isSidebar) && projectId && user?.id) {
      const sessionId = currentSessionId || "";
      const wsUrl = getAIWSEndApiMap("aiChatWS")
        .replace(":projectId", projectId)
        .replace(":studentId", user.id) + (sessionId ? `?session_id=${sessionId}` : "");
        
      console.log("Connecting to AI Service WebSocket:", wsUrl);
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log("Connected to AI Service WebSocket");
      };

      socket.onmessage = (event) => {
        const aiMessage: Message = {
          id: Date.now().toString(),
          sender: "ai",
          text: event.data,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, aiMessage]);
        setIsLoading(false);
      };

      socket.onclose = () => {
        console.log("Disconnected from AI Service WebSocket");
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
      };

      socket.onerror = (error) => {
        console.error("WebSocket Error:", error);
      };

      return () => {
        if (socketRef.current) {
          const s = socketRef.current;
          if (s.readyState === WebSocket.CONNECTING || s.readyState === WebSocket.OPEN) {
            s.close();
          }
          socketRef.current = null;
        }
      };
    }
    return undefined;
  }, [isOpen, isSidebar, projectId, user?.id, currentSessionId]);

  const handleSend = () => {
    if (!input.trim() || !socketRef.current) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      sender: "user",
      text: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    socketRef.current.send(input);
    setInput("");
    setIsLoading(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  const cleanMessageText = (text: string) => {
    if (text.startsWith("PROJECT DETAILS:")) {
      const parts = text.split("STUDENT MESSAGE:");
      return parts.length > 1 ? parts[1].trim() : text;
    }
    return text;
  };

  if (!isOpen && !isSidebar) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all z-50 flex items-center justify-center group"
        title="AI Assistant"
      >
        <Bot size={24} className="group-hover:scale-110 transition-transform" />
        <span className="ml-2 font-medium hidden group-hover:block whitespace-nowrap">AI Assistant</span>
      </button>
    );
  }

  return (
    <div
      className={cn(
        "bg-white dark:bg-[#0d1117] flex flex-col transition-all overflow-hidden",
        isSidebar 
          ? "h-full w-full border-l border-gray-200 dark:border-[#30363d]" 
          : cn(
              "fixed bottom-6 right-6 w-80 border border-gray-200 dark:border-[#30363d] rounded-xl shadow-2xl z-50",
              isMinimized ? "h-14" : "h-[500px]"
            )
      )}
    >
      {/* Header */}
      <div 
        className={cn(
          "p-3 border-b dark:border-[#30363d] flex items-center justify-between",
          isSidebar ? "bg-white dark:bg-[#161b22] text-gray-900 dark:text-[#f0f6fc]" : "bg-blue-600 text-white cursor-pointer"
        )} 
        onClick={() => !isSidebar && setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-2">
          {isHistoryView ? (
            <button 
              onClick={(e) => { e.stopPropagation(); setIsHistoryView(false); }}
              className={cn(
                "p-1 rounded transition-colors",
                isSidebar ? "hover:bg-gray-100 dark:hover:bg-[#30363d] text-gray-600 dark:text-[#8b949e]" : "hover:bg-white/20 text-white"
              )}
            >
              <ArrowLeft size={16} />
            </button>
          ) : (
            <Bot size={20} className={isSidebar ? "text-blue-600 dark:text-[#f0883e]" : "text-white"} />
          )}
          <span className="font-semibold text-sm">{isHistoryView ? "Chat History" : "AI Tutor"}</span>
        </div>
        
        <div className="flex items-center gap-1">
          {!isHistoryView && !isMinimized && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startNewChat();
                }}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
                title="New Chat"
              >
                <Plus size={16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  fetchSessions();
                  setIsHistoryView(true);
                }}
                className="p-1.5 hover:bg-white/20 rounded transition-colors"
                title="History"
              >
                <History size={16} />
              </button>
            </>
          )}
          {!isSidebar && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              className="p-1.5 hover:bg-white/20 rounded transition-colors"
            >
              {isMinimized ? <Maximize2 size={16} /> : <Minus size={16} />}
            </button>
          )}
          <button
            onClick={handleClose}
            className={cn(
              "p-1.5 rounded transition-colors",
              isSidebar ? "hover:bg-gray-100 dark:hover:bg-[#30363d] text-gray-500 dark:text-[#8b949e]" : "hover:bg-white/20 text-white"
            )}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages or History List */}
      {(!isMinimized || isSidebar) && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {isHistoryView ? (
            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0d1117] p-2 space-y-1">
              {sessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-[#8b949e] p-4">
                   <Clock size={32} className="mb-2 opacity-20" />
                   <p className="text-xs">No previous chats found.</p>
                </div>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => loadSession(s.session_id)}
                    className={cn(
                      "w-full text-left p-3 rounded-lg border transition-all flex items-center gap-3 group",
                      currentSessionId === s.session_id 
                        ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400" 
                        : "bg-white dark:bg-[#161b22] border-gray-100 dark:border-[#30363d] hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-[#c9d1d9]"
                    )}
                  >
                    <MessageSquare size={14} className="flex-shrink-0 opacity-50" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">Session: {s.session_id.slice(0, 8)}...</p>
                      {s.created_at && (
                        <p className="text-[10px] opacity-60 flex items-center gap-1 mt-0.5">
                          <Clock size={10} />
                          {new Date(s.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-[#0d1117]">
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-[#8b949e] text-center space-y-2">
                    <Bot size={40} className="mb-2 opacity-20" />
                    <p className="text-sm font-medium">Hello! I'm your AI Tutor.</p>
                    <p className="text-xs">Ask me anything about this project.</p>
                  </div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex items-start gap-2 max-w-[90%]",
                      msg.sender === "user" ? "ml-auto flex-row-reverse" : "mr-auto"
                    )}
                  >
                    <div
                      className={cn(
                        "p-2 rounded-full flex-shrink-0",
                        msg.sender === "user" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : "bg-white dark:bg-[#161b22] border dark:border-[#30363d] text-gray-600 dark:text-[#8b949e]"
                      )}
                    >
                      {msg.sender === "user" ? <User size={14} /> : <Bot size={14} />}
                    </div>
                    <div
                      className={cn(
                        "p-3 rounded-2xl text-sm shadow-sm",
                        msg.sender === "user" 
                          ? "bg-blue-600 text-white rounded-tr-none" 
                          : "bg-white dark:bg-[#161b22] text-gray-800 dark:text-[#c9d1d9] border dark:border-[#30363d] rounded-tl-none"
                      )}
                    >
                      <p className="whitespace-pre-wrap">{cleanMessageText(msg.text)}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex items-center gap-2">
                    <div className="bg-white dark:bg-[#161b22] border dark:border-[#30363d] p-2 rounded-full text-gray-400 dark:text-[#8b949e]">
                      <Bot size={14} />
                    </div>
                    <div className="bg-white dark:bg-[#161b22] border dark:border-[#30363d] p-3 rounded-2xl rounded-tl-none shadow-sm">
                      <Loader2 size={16} className="animate-spin text-gray-400 dark:text-[#8b949e]" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t dark:border-[#30363d] bg-white dark:bg-[#161b22]">
                <div className="relative flex items-center">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Ask your tutor..."
                    className="w-full pl-3 pr-10 py-2 border border-gray-200 dark:border-[#30363d] rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] text-sm resize-none"
                    rows={1}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    className="absolute right-2 p-1.5 text-blue-600 dark:text-[#f0883e] hover:bg-blue-50 dark:hover:bg-[#30363d] disabled:text-gray-300 dark:disabled:text-gray-600 rounded-lg transition-colors"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAssistantChat;
